import { useRef, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import Button from "../Buttons/Button";
import {
  ADD_ALL_LOCATIONS,
  LOADING_SUGGESTED_LOCATIONS,
  SUGGESTED_LOCATIONS_TITLE,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";

const SuggestedLocationsBar = ({
  suggestedLocations = [],
  isLoadingSuggestedLocations = false,
  onAddAllSuggestedLocations = () => {},
  onAddSuggestedLocation = () => {},
}) => {
  const { t } = useTranslationApp();
  const scrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        const { scrollWidth, clientWidth } = scrollRef.current;
        setShowArrows(scrollWidth > clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [suggestedLocations]);

  const onScrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const onScrollRight = () => {
    scrollRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        display: {
          xs: "none",
          md: "flex",
        },
        position: "absolute",
        bottom: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2,
        borderRadius: 99,
        backgroundColor: "background.paper",
        boxShadow: 3,
        alignItems: "center",
        gap: 1,
        pl: 2,
        pr: 2,
        py: 0.75,
        maxWidth: "calc(100% - 320px)",
        minWidth: 0,
        height: 48,
      }}
    >
      {isLoadingSuggestedLocations ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {t(LOADING_SUGGESTED_LOCATIONS)}
          </Typography>
        </Box>
      ) : (
        <>
          {/* SUGGESTED label */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "text.secondary",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t(SUGGESTED_LOCATIONS_TITLE).toUpperCase()}
          </Typography>

          {/* Left arrow */}
          {showArrows && (
            <IconButton
              size="small"
              onClick={onScrollLeft}
              sx={{ flexShrink: 0, p: 0, width: 6 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}

          {/* Horizontal scrollable location chips */}
          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflowX: "auto",
              flexShrink: 1,
              minWidth: 0,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {suggestedLocations.map((location, index) => {
              const label =
                location.city || location.region || location.country || "";
              return (
                <Button
                  key={index}
                  color="secondary"
                  endIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
                  sx={{
                    py: "3px !important",
                    px: "12px !important",
                    height: 28,
                    minWidth: "auto",
                    fontSize: "0.7rem !important",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                  onClick={() => onAddSuggestedLocation(location, index)}
                >
                  {label}
                </Button>
              );
            })}
          </Box>

          {/* Right arrow */}
          {showArrows && (
            <IconButton
              size="small"
              onClick={onScrollRight}
              sx={{ flexShrink: 0, p: 0, width: 12 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}

          <Button
            size="small"
            color="secondary"
            sx={{
              borderRadius: 99,
              py: "3px !important",
              px: "10px !important",
              height: 28,
              minWidth: "auto",
              fontSize: "0.7rem !important",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            onClick={onAddAllSuggestedLocations}
          >
            {t(ADD_ALL_LOCATIONS)}
          </Button>
        </>
      )}
    </Box>
  );
};

export default SuggestedLocationsBar;
